import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { EventSidebar } from "@/components/EventSidebar"
import { CommandPalette } from "@/components/CommandPalette"
import { FloatingActionButton } from "@/components/FloatingActionButton"
import { AIChatWidget } from "@/components/AIChatWidget"
import { TrialBanner } from "@/components/TrialBanner"
import { PastDueBanner } from "@/components/PastDueBanner"
import { NoSubscriptionBanner } from "@/components/NoSubscriptionBanner"
import { Bell, User, LogOut, Settings, CreditCard, Receipt } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useNavigate, useParams, Outlet } from "react-router-dom"
import { useState, useEffect } from "react"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAuth } from "@/contexts/AuthContext"
import { useToast } from "@/hooks/use-toast"
import { useAIChat } from "@/contexts/AIChatContext"

export function EventLayout() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user, signOut } = useAuth()
  const { toast } = useToast()
  const { openChat } = useAIChat()
  const [commandOpen, setCommandOpen] = useState(false)

  // Função para fazer logout
  const handleLogout = async () => {
    try {
      await signOut()
      toast({
        title: "Logout realizado",
        description: "Você saiu com sucesso da sua conta"
      })
      navigate("/")
    } catch (error) {
      toast({
        title: "Erro ao sair",
        description: "Não foi possível realizar o logout",
        variant: "destructive"
      })
    }
  }

  // Atalho Cmd+K / Ctrl+K para abrir Command Palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandOpen(true)
      }
      // Atalho Shift+? para abrir chat de IA diretamente
      if (e.shiftKey && e.key === '?') {
        e.preventDefault()
        openChat()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openChat])

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <EventSidebar />
        
        <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          {/* Header */}
          <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <SidebarTrigger className="h-11 w-11 sm:h-9 sm:w-9" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-brand-purple font-omnes truncate">Gestão do Evento</h2>
                <p className="text-sm text-muted-foreground hidden sm:block">Gerencie todos os aspectos do seu evento</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => navigate(`/evento/${id}/notificacoes`)}
              >
                <Bell className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="relative">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user?.user_metadata?.avatar_url} />
                        <AvatarFallback>
                          {user?.email?.substring(0, 2).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {user?.user_metadata?.full_name || 'Usuário'}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                          {user?.email}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/evento/${id}/configuracoes`)}>
                    <Settings className="mr-2 h-4 w-4" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/planos")}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Assinatura
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pagamentos")}>
                    <Receipt className="mr-2 h-4 w-4" />
                    Pagamentos
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          
          <TrialBanner />
          <PastDueBanner />
          <NoSubscriptionBanner />

          {/* Main Content */}
          <main className="flex-1 min-w-0 p-4 sm:p-6">
            <Outlet />
          </main>
        </div>

        {/* Command Palette */}
        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />

        {/* Floating Action Button */}
        <FloatingActionButton />

        {/* AI Chat Widget */}
        <AIChatWidget />
      </div>
    </SidebarProvider>
  )
}